import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FileTrigger, Label, TextField } from "react-aria-components";
import { File as FileIcon, FileImage, FileVideo, Loader2, Pencil, Plus, Trash2, X } from "lucide-react";
import { PM_MEDIA_EXTENSIONS, type PmMicroScenarioView, type PmTask, type PmTaskMedia } from "@arc/contracts";
import { Dialog, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import type { AuthSession } from "../../auth/keycloak.js";
import { formatDateTime } from "./pm-labels.js";
import { useInstituteLabel } from "./pm-institutes.js";
import { PmTagPicker } from "./pm-tags.js";
import { activityText } from "./pm-activity.js";
import { useMountedRef, usePmNotices } from "./PmNotices.js";
import { PmNote } from "./PmNote.js";
import { fetchPmMediaUrl, isStale, usePmMediaPreview, usePmTaskActivity, usePmTaskAnnotations, usePmTaskAnnotationWrites, usePmTaskWrites } from "./usePmBoard.js";

const WHOLE_MS = "whole";

/**
 * Task details (ADR-055 phase 6, mockup "3b"): description, media, notes on the left; column, step, meeting flag, the
 * task's own history and delete on the right. Every PM user may change all of it (D2); edit, move and delete carry
 * the task's version (D9) — a 409 says "changed in the meantime" and the refetch shows the new state.
 *
 * The task comes from the MS view the board already holds, so the dialog never shows a second, diverging copy; a task
 * that is not in it (deleted, or from an earlier phase) is "not found" — the archive is read through the API, not here.
 */
export function PmTaskDialog({
  session,
  meId,
  data,
  taskId,
  onClose,
}: {
  session: AuthSession;
  meId: number;
  data: PmMicroScenarioView;
  taskId: number;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const task = data.tasks.find((x) => x.id === taskId);
  if (!task) {
    return (
      <Dialog isOpen onOpenChange={(open) => !open && onClose()}>
        <DialogTitle>{t("pmBoard.taskDetail.notFound")}</DialogTitle>
        <div className="flex justify-end">
          <Button variant="default" onPress={onClose}>
            {t("pmBoard.taskDetail.close")}
          </Button>
        </div>
      </Dialog>
    );
  }
  return <TaskDetails session={session} meId={meId} data={data} task={task} onClose={onClose} />;
}

function TaskDetails({ session, meId, data, task, onClose }: { session: AuthSession; meId: number; data: PmMicroScenarioView; task: PmTask; onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const instituteLabel = useInstituteLabel();
  const writes = usePmTaskWrites(session);
  const mounted = useMountedRef();
  const notices = usePmNotices();
  const notes = usePmTaskAnnotations(session, task.id);
  const noteWrites = usePmTaskAnnotationWrites(session);
  const history = usePmTaskActivity(session, task.id);
  // The version is captured when the form OPENS (diff panel): a refetch meanwhile must not hand the save someone else's
  // newer version, or the lock (D9) would let this stale form overwrite their edit without a 409.
  // After a 409 the draft is kept and `rebase` is set: the next Save is the user's explicit "overwrite with my text",
  // sent with the version the refetch brought — never the stale one again (that could only 409 for ever).
  const [editing, setEditing] = useState<{ title: string; description: string; version: number; rebase: boolean } | null>(null);
  const [draft, setDraft] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState<string[]>([]);
  const [failedUploads, setFailedUploads] = useState<string[]>([]);
  const [removeMedia, setRemoveMedia] = useState<PmTaskMedia | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const step = data.steps.find((s) => s.id === task.stepId);
  // Running uploads count too (diff panel): closing the dialog mid-upload would unmount the one place a failure shows.
  const busy = writes.update.isPending || writes.move.isPending || writes.meeting.isPending || writes.tags.isPending || writes.remove.isPending || uploading.length > 0;
  // While the edit form is open the side panel is locked: its own write would bump the version under the draft and
  // turn the draft's Save into a 409 the user caused themselves (diff panel).
  const propsLocked = busy || editing !== null;
  // As for the form: the tag edit sends the version it started from, so a change by someone else meanwhile is a 409, not lost.
  const tagsVersion = useRef(task.version);

  const onWriteError = (e: Error) => setError(isStale(e) ? t("pmBoard.kanban.stale") : t("pmBoard.taskDetail.saveFailed"));
  const save = () => {
    if (!editing) return;
    setError(null);
    writes.update.mutate(
      { id: task.id, version: editing.rebase ? task.version : editing.version, title: editing.title.trim(), description: editing.description },
      {
        onSuccess: () => setEditing(null),
        onError: (e) => {
          if (isStale(e)) {
            setEditing((ed) => ed && { ...ed, rebase: true });
            setError(t("pmBoard.taskDetail.editStale"));
          } else onWriteError(e);
        },
      },
    );
  };
  const upload = async (files: File[]) => {
    setFailedUploads([]);
    for (const file of files) {
      setUploading((u) => [...u, file.name]);
      try {
        await writes.upload.mutateAsync({ taskId: task.id, file });
      } catch {
        // Still open → say it here; gone (e.g. browser Back) → a board notice, so the failure is never lost.
        if (mounted.current) setFailedUploads((f) => [...f, file.name]);
        else notices.report(t("pmBoard.notices.uploadOrphaned", { name: file.name, task: task.title }));
      } finally {
        if (mounted.current) setUploading((u) => u.filter((n) => n !== file.name));
      }
    }
  };
  const open = async (m: PmTaskMedia) => {
    // Opened synchronously inside the click, so no popup blocker stands in the way; the presigned URL follows.
    const win = window.open("", "_blank");
    try {
      const url = await fetchPmMediaUrl(m.id);
      if (win) {
        win.opener = null;
        win.location.href = url;
      } else window.location.assign(url);
    } catch {
      win?.close();
      setError(t("pmBoard.taskDetail.openFailed"));
    }
  };

  return (
    <Dialog isOpen size="xl" isDismissable={!busy} isKeyboardDismissDisabled={busy} onOpenChange={(o) => !o && !busy && onClose()} className="gap-0 p-0">
      <div className="border-border flex items-start gap-3 border-b px-6 pt-5 pb-4">
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          {/* Tags and the meeting mark are not repeated here: the side panel shows and edits them. */}
          <span className="text-muted-foreground text-xs">{step ? `${step.number} · ${step.title}` : t("pmBoard.kanban.wholeMs")}</span>
          <DialogTitle className="break-words">{task.title}</DialogTitle>
        </div>
        {editing === null ? (
          <Button variant="outline" onPress={() => setEditing({ title: task.title, description: task.description, version: task.version, rebase: false })}>
            <Pencil aria-hidden="true" />
            {t("pmBoard.taskDetail.edit")}
          </Button>
        ) : null}
        <Button variant="ghost" size="icon" aria-label={t("pmBoard.taskDetail.close")} onPress={onClose} isDisabled={busy}>
          <X aria-hidden="true" />
        </Button>
      </div>

      {error ? (
        <p role="alert" className="text-destructive m-0 px-6 pt-3 text-sm">
          {error}
        </p>
      ) : null}

      <div className="flex flex-wrap">
        <div className="flex min-w-0 flex-[999_1_30rem] flex-col gap-5 px-6 py-5">
          {editing ? (
            <div className="flex flex-col gap-3">
              <TextField value={editing.title} onChange={(v) => setEditing({ ...editing, title: v })} isRequired className="flex flex-col gap-1.5">
                <Label className="text-sm font-medium">{t("pmBoard.taskDetail.titleLabel")}</Label>
                <Input maxLength={500} autoFocus />
              </TextField>
              <TextField value={editing.description} onChange={(v) => setEditing({ ...editing, description: v })} className="flex flex-col gap-1.5">
                <Label className="text-sm font-medium">{t("pmBoard.taskDetail.description")}</Label>
                <Textarea rows={5} maxLength={20_000} />
              </TextField>
              <div className="flex justify-end gap-2">
                <Button variant="ghost" onPress={() => setEditing(null)} isDisabled={writes.update.isPending}>
                  {t("pmBoard.taskDetail.cancel")}
                </Button>
                <Button variant="default" onPress={save} isDisabled={writes.update.isPending || !editing.title.trim()}>
                  {t("pmBoard.taskDetail.save")}
                </Button>
              </div>
            </div>
          ) : (
            <section className="flex flex-col gap-1.5" aria-labelledby="pm-td-desc">
              <h3 id="pm-td-desc" className="text-sm font-semibold">
                {t("pmBoard.taskDetail.description")}
              </h3>
              <p className="m-0 text-sm break-words whitespace-pre-wrap">{task.description || <span className="text-muted-foreground">{t("pmBoard.taskDetail.noDescription")}</span>}</p>
            </section>
          )}

          <section className="flex flex-col gap-2" aria-labelledby="pm-td-media">
            <div className="flex items-center gap-2">
              <h3 id="pm-td-media" className="flex-1 text-sm font-semibold">
                {t("pmBoard.taskDetail.media")}
              </h3>
              <FileTrigger allowsMultiple acceptedFileTypes={[...PM_MEDIA_EXTENSIONS]} onSelect={(list) => list && void upload(Array.from(list))}>
                <Button variant="outline" size="sm">
                  <Plus aria-hidden="true" />
                  {t("pmBoard.taskDetail.addMedia")}
                </Button>
              </FileTrigger>
            </div>
            {task.media.length === 0 && uploading.length === 0 ? <p className="text-muted-foreground m-0 text-sm">{t("pmBoard.taskDetail.noMedia")}</p> : null}
            <ul className="m-0 grid list-none grid-cols-[repeat(auto-fill,minmax(8rem,1fr))] gap-2.5 p-0">
              {task.media.map((m) => (
                <li key={m.id} className="flex flex-col gap-1">
                  <Button variant="outline" onPress={() => void open(m)} aria-label={t("pmBoard.taskDetail.openMedia", { name: m.fileName })} className="bg-muted h-20 w-full overflow-hidden p-0">
                    <MediaPreview session={session} media={m} />
                  </Button>
                  <span className="flex items-center gap-1">
                    <span className="text-muted-foreground min-w-0 flex-1 truncate text-xs" title={m.fileName}>
                      {m.fileName}
                    </span>
                    <Button variant="ghost" size="icon-xs" aria-label={t("pmBoard.taskDetail.removeMedia", { name: m.fileName })} onPress={() => setRemoveMedia(m)}>
                      <X aria-hidden="true" />
                    </Button>
                  </span>
                </li>
              ))}
            </ul>
            {uploading.map((n) => (
              <p key={n} role="status" className="text-muted-foreground m-0 flex items-center gap-2 text-sm">
                <Loader2 aria-hidden="true" className="size-4 animate-spin" />
                {t("pmBoard.taskDetail.uploading", { name: n })}
              </p>
            ))}
            {failedUploads.map((n) => (
              <p key={n} role="alert" className="text-destructive m-0 text-sm">
                {t("pmBoard.taskDetail.uploadFailed", { name: n })}
              </p>
            ))}
          </section>

          <section className="flex flex-col gap-3" aria-labelledby="pm-td-notes">
            <h3 id="pm-td-notes" className="text-sm font-semibold">
              {t("pmBoard.taskDetail.notes")}
            </h3>
            {(notes.data ?? []).map((n) => (
              <PmNote
                key={n.id}
                note={n}
                canEdit={n.author.userId === meId}
                canDelete={n.author.userId === meId || data.isAdmin}
                onUpdate={(text) => noteWrites.update.mutateAsync({ id: n.id, text })}
                onDelete={() => noteWrites.remove.mutateAsync({ id: n.id })}
              />
            ))}
            <TextField value={draft} onChange={setDraft} className="flex flex-col gap-1.5">
              <Label className="sr-only">{t("pmBoard.taskDetail.newNote")}</Label>
              <Textarea rows={2} placeholder={t("pmBoard.note.placeholder")} maxLength={10_000} />
            </TextField>
            {noteWrites.create.isError ? (
              <p role="alert" className="text-destructive m-0 text-sm">
                {t("pmBoard.taskDetail.noteFailed")}
              </p>
            ) : null}
            <Button
              variant="outline"
              className="self-end"
              isDisabled={!draft.trim() || noteWrites.create.isPending}
              onPress={() => noteWrites.create.mutate({ taskId: task.id, text: draft.trim() }, { onSuccess: () => setDraft("") })}
            >
              {t("pmBoard.taskDetail.saveNote")}
            </Button>
          </section>
        </div>

        <aside aria-label={t("pmBoard.taskDetail.properties")} className="bg-muted/60 flex min-w-0 flex-[1_1_17rem] flex-col gap-4 px-6 py-5">
          <Select
            selectedKey={String(task.columnId)}
            isDisabled={propsLocked}
            onSelectionChange={(k) => {
              if (k === null || Number(k) === task.columnId) return;
              setError(null);
              writes.move.mutate({ id: task.id, version: task.version, columnId: Number(k) }, { onError: onWriteError });
            }}
            className="flex flex-col gap-1.5"
          >
            <Label className="text-sm font-medium">{t("pmBoard.taskDetail.column")}</Label>
            <SelectTrigger />
            <SelectContent>
              {data.columns.map((c) => (
                <SelectItem key={c.id} id={String(c.id)}>
                  {c.title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            selectedKey={task.stepId === null ? WHOLE_MS : String(task.stepId)}
            isDisabled={propsLocked}
            onSelectionChange={(k) => {
              if (k === null) return;
              const stepId = k === WHOLE_MS ? null : Number(k);
              if (stepId === task.stepId) return;
              setError(null);
              writes.update.mutate({ id: task.id, version: task.version, stepId }, { onError: onWriteError });
            }}
            className="flex flex-col gap-1.5"
          >
            <Label className="text-sm font-medium">{t("pmBoard.taskDetail.step")}</Label>
            <SelectTrigger />
            <SelectContent>
              <SelectItem id={WHOLE_MS}>{t("pmBoard.newTaskDialog.wholeMs")}</SelectItem>
              {data.steps.map((s) => (
                <SelectItem key={s.id} id={String(s.id)}>{`${s.number} · ${s.title}`}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <PmTagPicker
            tags={data.tags}
            // While it saves, the list being saved: the task only catches up with the refetch.
            selected={writes.tags.isPending && writes.tags.variables ? writes.tags.variables.tagIds : task.tagIds}
            isDisabled={propsLocked}
            onOpen={() => {
              tagsVersion.current = task.version;
            }}
            onCommit={(tagIds) => {
              setError(null);
              writes.tags.mutate({ id: task.id, version: tagsVersion.current, tagIds }, { onError: onWriteError });
            }}
          />

          <Switch isSelected={task.flaggedForMeeting} isDisabled={propsLocked} onChange={(flagged) => writes.meeting.mutate({ id: task.id, flagged }, { onError: onWriteError })}>
            {t("pmBoard.taskDetail.meeting")}
          </Switch>

          <hr className="border-border m-0 border-t" />

          <span className="text-muted-foreground flex flex-col text-xs">
            <span>
              {t("pmBoard.taskDetail.created", {
                name: task.createdBy.name,
                institute: instituteLabel(task.createdBy),
                date: formatDateTime(task.createdAt, i18n.language),
              })}
            </span>
            {task.updatedAt !== task.createdAt ? <span>{t("pmBoard.taskDetail.updated", { date: formatDateTime(task.updatedAt, i18n.language) })}</span> : null}
          </span>

          {/* The history takes the height the rest of the dialog leaves and scrolls inside it: absolutely placed, it adds
              nothing to the dialog's own height. */}
          <section className="flex min-h-0 flex-1 flex-col gap-2.5" aria-labelledby="pm-td-history">
            <h3 id="pm-td-history" className="text-sm font-semibold">
              {t("pmBoard.taskDetail.history")}
            </h3>
            {history.data && history.data.length === 0 ? <p className="text-muted-foreground m-0 text-sm">{t("pmBoard.taskDetail.noHistory")}</p> : null}
            {history.data && history.data.length > 0 ? (
              <div className="relative min-h-24 flex-1">
                <ol tabIndex={0} aria-labelledby="pm-td-history" className="absolute inset-0 m-0 flex list-none flex-col gap-2.5 overflow-y-auto p-0 [scrollbar-color:var(--border-strong)_transparent]">
                  {history.data.map((a) => (
                    <li key={a.id} className="border-border flex flex-col gap-0.5 border-l-2 pl-3">
                      <span className="text-[0.8rem]">
                        <strong className="font-semibold">{t("pmBoard.activity.actor", { institute: instituteLabel(a.actor), name: a.actor.name })}</strong>{" "}
                        {activityText(t, a)}
                      </span>
                      <span className="text-muted-foreground text-xs">{formatDateTime(a.createdAt, i18n.language)}</span>
                    </li>
                  ))}
                </ol>
              </div>
            ) : null}
          </section>

          <div className="mt-auto flex flex-col gap-1.5 pt-2">
            <Button variant="destructive" className="self-start" onPress={() => setConfirmDelete(true)} isDisabled={busy}>
              <Trash2 aria-hidden="true" />
              {t("pmBoard.taskDetail.delete")}
            </Button>
            <span className="text-muted-foreground text-xs">{t("pmBoard.taskDetail.deleteHint")}</span>
          </div>
        </aside>
      </div>

      <ConfirmDialog
        isOpen={removeMedia !== null}
        title={t("pmBoard.taskDetail.removeMediaTitle")}
        message={t("pmBoard.taskDetail.removeMediaBody", { name: removeMedia?.fileName ?? "" })}
        confirmLabel={t("pmBoard.note.delete")}
        destructive
        isPending={writes.removeMedia.isPending}
        onConfirm={() =>
          removeMedia &&
          writes.removeMedia.mutate(
            { id: removeMedia.id },
            {
              onSuccess: () => setRemoveMedia(null),
              onError: () => {
                setRemoveMedia(null);
                setError(t("pmBoard.taskDetail.removeMediaFailed"));
              },
            },
          )
        }
        onCancel={() => setRemoveMedia(null)}
      />
      <ConfirmDialog
        isOpen={confirmDelete}
        title={t("pmBoard.taskDetail.deleteTitle")}
        message={t("pmBoard.taskDetail.deleteBody", { title: task.title })}
        confirmLabel={t("pmBoard.taskDetail.delete")}
        destructive
        isPending={writes.remove.isPending}
        // `mutateAsync`, not a per-call `onSuccess` (diff panel): the refetch after the delete drops the task from the view,
        // which unmounts this component before a `mutate` callback would run — the dialog would land on "not found"
        // instead of closing. The awaited promise settles regardless.
        onConfirm={() => {
          writes.remove.mutateAsync({ id: task.id, version: task.version }).then(onClose, (e: unknown) => {
            setConfirmDelete(false);
            setError(isStale(e) ? t("pmBoard.kanban.stale") : t("pmBoard.taskDetail.deleteFailed"));
          });
        }}
        onCancel={() => setConfirmDelete(false)}
      />
    </Dialog>
  );
}

/** An image shows itself (`alt` empty: the button around it names the file); everything else, or a failed load, its icon. */
function MediaPreview({ session, media }: { session: AuthSession; media: PmTaskMedia }) {
  const isImage = media.mimeType.startsWith("image/");
  const preview = usePmMediaPreview(session, media.id, isImage);
  const [broken, setBroken] = useState(false);
  if (preview.data && !broken) return <img src={preview.data} alt="" onError={() => setBroken(true)} className="size-full object-cover" />;
  return <MediaIcon mime={media.mimeType} />;
}

function MediaIcon({ mime }: { mime: string }) {
  const Icon = mime.startsWith("image/") ? FileImage : mime.startsWith("video/") ? FileVideo : FileIcon;
  return <Icon aria-hidden="true" className="text-muted-foreground size-7" />;
}
